import { useEffect, useState } from 'react';
import { FileBrowser } from '../components/files';
import { ExtractionRunsPanel } from '../components/extractions/ExtractionRunsPanel';
import { api, ProjectExtraction } from '../api';

const BANNER_DISMISSED_KEY = 'fakto-upload-banner-dismissed';

type UploadsView = 'files' | 'imports';

interface UploadsPageProps {
  currentFolderId: string | null;
  onFolderChange: (folderId: string | null) => void;
  rootFolderId?: string | null;
  projectId: string;
  projectName?: string;
  onBack?: () => void;
}

export function UploadsPage({
  currentFolderId,
  onFolderChange,
  rootFolderId,
  projectId,
  projectName,
  onBack,
}: UploadsPageProps) {
  const [bannerDismissed, setBannerDismissed] = useState(
    () => localStorage.getItem(BANNER_DISMISSED_KEY) === 'true'
  );
  const [view, setView] = useState<UploadsView>('files');
  // Automated imports mapped to this project (superadmin-configured). The
  // switch only appears when there is at least one; a fetch failure just
  // hides it — the file browser must never depend on the ledger.
  const [extractions, setExtractions] = useState<ProjectExtraction[]>([]);
  const [ledgerConfigured, setLedgerConfigured] = useState(true);

  useEffect(() => {
    let cancelled = false;
    // Reset everything project-scoped before the fetch: the previous project's
    // imports must not stay clickable (its extraction ids are not mapped here).
    setView('files');
    setExtractions([]);
    setLedgerConfigured(true);
    api
      .listProjectExtractions(projectId)
      .then((res) => {
        if (cancelled) return;
        setExtractions(res.extractions);
        setLedgerConfigured(res.ledger_configured);
      })
      .catch(() => {
        if (!cancelled) setExtractions([]);
      });
    return () => {
      cancelled = true;
    };
  }, [projectId]);

  const dismissBanner = () => {
    setBannerDismissed(true);
    localStorage.setItem(BANNER_DISMISSED_KEY, 'true');
  };

  const hasImports = extractions.length > 0;

  return (
    <div className="page">
      {onBack && (
        <button className="back-button" onClick={onBack}>
          <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
            <path d="M10 12L6 8l4-4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
          All projects
        </button>
      )}
      {!bannerDismissed && view === 'files' && (
        <div className="upload-info-banner">
          <div className="upload-info-banner-icon">
            <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
              <path d="M10 1.667A8.333 8.333 0 1 0 18.333 10 8.342 8.342 0 0 0 10 1.667Zm0 15A6.667 6.667 0 1 1 16.667 10 6.674 6.674 0 0 1 10 16.667Z" fill="currentColor"/>
              <path d="M10 5.833a1.042 1.042 0 1 0 0 2.084 1.042 1.042 0 0 0 0-2.084ZM10 9.167a.833.833 0 0 0-.833.833v3.333a.833.833 0 1 0 1.666 0V10A.833.833 0 0 0 10 9.167Z" fill="currentColor"/>
            </svg>
          </div>
          <div className="upload-info-banner-content">
            <strong>Welcome to your secure upload space</strong>
            <span>
              This is the safest and easiest way to share your documents with Fakto. All files are
              encrypted and transmitted securely — simply upload, add any notes or context you'd like,
              and we take care of the rest.
            </span>
          </div>
          <button
            className="upload-info-banner-close"
            onClick={dismissBanner}
            aria-label="Dismiss"
          >
            &times;
          </button>
        </div>
      )}
      <div className="page-header">
        <h1>{projectName || 'Uploads'}</h1>
        <p className="subtitle">
          {view === 'files' ? 'Manage your uploaded files' : 'Files imported automatically for this project'}
        </p>
      </div>
      {hasImports && (
        <div className="uploads-view-switch" role="tablist" aria-label="Uploads view">
          <button
            type="button"
            role="tab"
            aria-selected={view === 'files'}
            className={view === 'files' ? 'active' : ''}
            onClick={() => setView('files')}
          >
            Your files
          </button>
          <button
            type="button"
            role="tab"
            aria-selected={view === 'imports'}
            className={view === 'imports' ? 'active' : ''}
            onClick={() => setView('imports')}
          >
            Automated imports
          </button>
        </div>
      )}
      {view === 'files' ? (
        <FileBrowser
          currentFolderId={currentFolderId}
          onFolderChange={onFolderChange}
          rootFolderId={rootFolderId}
          projectId={projectId}
        />
      ) : (
        <ExtractionRunsPanel projectId={projectId} extractions={extractions} ledgerConfigured={ledgerConfigured} />
      )}
    </div>
  );
}
